import { useState, useRef, useCallback } from 'react'
import { Plus, Trash, Info, Lock } from '@phosphor-icons/react'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import MoneyInput from '../MoneyInput'
import {
  FeeCategory,
  CATEGORY_LABELS,
  CATEGORY_DESCRIPTIONS,
} from '../../constants/feeTemplates'
import { FEE_UNITS, FEE_UNIT_MAP, DEFAULT_FEE_UNIT, computeFeeSubtotal, type FeeUnit } from '../../constants/feeUnits'
import { formatCurrency } from '../../hooks/useExchangeRate'
import type { CargoMetrics } from '../../utils/cargoMetrics'

// Supported currencies
export type OfferCurrency = 'BRL' | 'USD' | 'EUR' | 'GBP' | 'CNY'

export const CURRENCY_OPTIONS: { code: OfferCurrency; symbol: string }[] = [
  { code: 'BRL', symbol: 'R$' },
  { code: 'USD', symbol: '$' },
  { code: 'EUR', symbol: '€' },
  { code: 'GBP', symbol: '£' },
  { code: 'CNY', symbol: '¥' },
]

export interface BreakdownItem {
  id: string
  name: string
  category: FeeCategory
  value: number           // rate entered by the FF (per-unit when unit ≠ FORFAIT)
  currency: OfferCurrency
  unit?: FeeUnit          // billing unit (FORFAIT when absent / legacy items)
  isCustom?: boolean
  isPlatformFee?: boolean
}

interface FeeBreakdownProps {
  breakdown: BreakdownItem[]
  onChange: (breakdown: BreakdownItem[]) => void
  currency?: OfferCurrency
  cargo: CargoMetrics
}

/**
 * FeeBreakdown — fees are added manually by the user; only platform fees are
 * auto-injected (and shown read-only). User-saved templates may apply a batch
 * of fees via the SavedTemplatesPanel above this component.
 */
export function FeeBreakdown({
  breakdown,
  onChange,
  currency = 'USD',
  cargo,
}: FeeBreakdownProps) {
  const [customFee, setCustomFee] = useState({
    name: '',
    category: 'additional' as FeeCategory,
    unit: DEFAULT_FEE_UNIT as FeeUnit,
  })
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map())

  // Ordered list of editable fee IDs by category for keyboard nav.
  const getOrderedFeeIds = useCallback((): string[] => {
    const categories: FeeCategory[] = ['freight', 'origin', 'destination', 'additional']
    const orderedIds: string[] = []
    categories.forEach(category => {
      breakdown
        .filter(item => !item.isPlatformFee && item.category === category)
        .forEach(fee => orderedIds.push(fee.id))
    })
    return orderedIds
  }, [breakdown])

  const handleKeyDown = useCallback((e: React.KeyboardEvent, currentId: string) => {
    const ids = getOrderedFeeIds()
    const idx = ids.indexOf(currentId)
    if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
      if (idx !== -1 && idx < ids.length - 1) {
        const next = inputRefs.current.get(ids[idx + 1])
        if (next) { e.preventDefault(); next.focus() }
      }
    } else if (e.key === 'Tab' && e.shiftKey) {
      if (idx > 0) {
        const prev = inputRefs.current.get(ids[idx - 1])
        if (prev) { e.preventDefault(); prev.focus() }
      }
    }
  }, [getOrderedFeeIds])

  const setInputRef = useCallback((id: string, el: HTMLInputElement | null) => {
    if (el) inputRefs.current.set(id, el)
    else inputRefs.current.delete(id)
  }, [])

  const updateFeeValue = (id: string, value: number) => {
    onChange(breakdown.map(item => item.id === id ? { ...item, value: Math.max(0, value) } : item))
  }

  const updateFeeCurrency = (id: string, newCurrency: OfferCurrency) => {
    onChange(breakdown.map(item => item.id === id ? { ...item, currency: newCurrency } : item))
  }

  const updateFeeUnit = (id: string, newUnit: FeeUnit) => {
    onChange(breakdown.map(item => item.id === id ? { ...item, unit: newUnit } : item))
  }

  const addCustomFee = () => {
    if (!customFee.name.trim()) return
    const id = `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    onChange([
      ...breakdown,
      {
        id,
        name: customFee.name.trim(),
        category: customFee.category,
        value: 0,
        currency,
        unit: customFee.unit,
        isCustom: true,
      },
    ])
    setCustomFee({ name: '', category: 'additional', unit: DEFAULT_FEE_UNIT })
    requestAnimationFrame(() => {
      const input = inputRefs.current.get(id)
      if (input) input.focus()
    })
  }

  const removeFee = (id: string) => {
    onChange(breakdown.filter(item => item.id !== id))
  }

  const renderCategoryFees = (category: FeeCategory) => {
    const fees = breakdown.filter(item => !item.isPlatformFee && item.category === category)
    if (fees.length === 0) return null

    return (
      <div key={category} className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="mb-4">
          <h3 className="text-base font-semibold text-gray-900">
            {CATEGORY_LABELS[category]}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {CATEGORY_DESCRIPTIONS[category]}
          </p>
        </div>

        <div className="space-y-2.5">
          {fees.map(fee => {
            const unit = fee.unit || DEFAULT_FEE_UNIT
            const unitDef = FEE_UNIT_MAP[unit]
            const subtotal = computeFeeSubtotal(fee.value || 0, unit, cargo)
            const billableQty = unitDef.billable(cargo)
            return (
              <div
                key={fee.id}
                className="flex flex-wrap items-center gap-2 p-3 rounded-lg border bg-gray-50 border-gray-200"
              >
                <div className="flex-1 min-w-[140px]">
                  <label className="text-sm font-medium text-gray-900">{fee.name}</label>
                  {fee.isCustom && (
                    <p className="text-xs text-blue-600">Taxa personalizada</p>
                  )}
                </div>

                <select
                  value={unit}
                  onChange={(e) => updateFeeUnit(fee.id, e.target.value as FeeUnit)}
                  className="h-8 px-2 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  title="Unidade da taxa"
                >
                  {FEE_UNITS.map(u => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </select>

                <div className="flex items-center gap-1">
                  <MoneyInput
                    ref={(el) => setInputRef(fee.id, el)}
                    value={fee.value || undefined}
                    onChange={(val) => updateFeeValue(fee.id, val)}
                    currency={fee.currency}
                    size="sm"
                    className="w-28"
                    onKeyDown={(e) => handleKeyDown(e, fee.id)}
                  />
                  <select
                    value={fee.currency}
                    onChange={(e) => updateFeeCurrency(fee.id, e.target.value as OfferCurrency)}
                    className="h-8 w-16 px-1 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    title="Moeda"
                  >
                    {CURRENCY_OPTIONS.map(opt => (
                      <option key={opt.code} value={opt.code}>{opt.code}</option>
                    ))}
                  </select>
                  <span className="text-xs text-gray-500 ml-1 whitespace-nowrap">{unitDef.shortLabel}</span>
                </div>

                <div className="text-right min-w-[110px] tabular-nums">
                  <div className="text-sm font-semibold text-emerald-700">
                    {formatCurrency(subtotal, fee.currency)}
                  </div>
                  {unit !== 'FORFAIT' && unit !== 'PER_DOCUMENT' && (
                    <div className="text-[10px] text-gray-500">
                      × {billableQty.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => removeFee(fee.id)}
                  className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-100 rounded transition-colors"
                  title="Remover taxa"
                >
                  <Trash size={16} />
                </button>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const categories: FeeCategory[] = ['freight', 'origin', 'destination', 'additional']
  const platformFees = breakdown.filter(item => item.isPlatformFee)
  const hasUserFees = breakdown.some(item => !item.isPlatformFee)

  return (
    <div className="space-y-4">
      {/* Platform Fees (read-only, auto-applied) */}
      {platformFees.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-amber-900">
                Taxas da Plataforma
              </h3>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-200 text-amber-800 text-xs font-medium rounded-full">
                <Lock size={10} weight="fill" />
                Automático
              </span>
            </div>
            <p className="text-xs text-amber-700 mt-0.5">
              Aplicadas automaticamente, não podem ser alteradas.
            </p>
          </div>

          <div className="space-y-2.5">
            {platformFees.map(fee => (
              <div
                key={fee.id}
                className="flex items-center gap-3 p-3 rounded-lg border bg-amber-100/50 border-amber-300"
              >
                <div className="flex items-center justify-center w-4 h-4">
                  <Lock size={14} weight="fill" className="text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <label className="text-sm font-medium text-amber-900">{fee.name}</label>
                  <p className="text-xs text-amber-600">Taxa automática da plataforma</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-8 px-3 flex items-center bg-amber-200/50 border border-amber-300 rounded text-sm font-medium text-amber-900">
                    $ {fee.value.toFixed(2)} USD
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <Info size={20} color="#2563eb" style={{ flexShrink: 0, marginTop: 2 }} />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-blue-900">
            Adicione cada taxa que se aplica a esta proposta. Use um template salvo
            acima para reaproveitar combinações usadas em propostas anteriores.
          </p>
        </div>
      </div>

      {/* User-added fees grouped by category */}
      {hasUserFees && categories.map(category => renderCategoryFees(category))}

      {/* Add Custom Fee */}
      <div className="bg-white border border-dashed border-gray-300 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Adicionar Taxa</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Nome da Taxa
            </label>
            <Input
              type="text"
              placeholder="Ex: Frete Marítimo, THC, Armazenagem"
              value={customFee.name}
              onChange={e => setCustomFee({ ...customFee, name: e.target.value })}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addCustomFee()
                }
              }}
            />
          </div>
          <div className="w-56">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Unidade
            </label>
            <select
              value={customFee.unit}
              onChange={e => setCustomFee({ ...customFee, unit: e.target.value as FeeUnit })}
              className="w-full h-10 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {FEE_UNITS.map(u => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
          </div>
          <div className="w-44">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Categoria
            </label>
            <select
              value={customFee.category}
              onChange={e => setCustomFee({ ...customFee, category: e.target.value as FeeCategory })}
              className="w-full h-10 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="freight">Frete</option>
              <option value="origin">Origem</option>
              <option value="destination">Destino</option>
              <option value="additional">Adicional</option>
            </select>
          </div>
          <Button
            type="button"
            onClick={addCustomFee}
            disabled={!customFee.name.trim()}
            className="gap-2"
            size="default"
          >
            <Plus size={16} />
            Adicionar
          </Button>
        </div>
      </div>
    </div>
  )
}
